import { useState, useEffect } from 'react'
import { Link, router } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { StageBadge } from '@/components/shared/StageBadge'
import { ProgressStepper } from '@/components/shared/ProgressStepper'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { formatCurrency } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import { FormationDetailed, FormationEntity, FormationSupports, FormationProposedName } from '@/types'
import { AddPrincipalSheet } from './sheets/AddPrincipalSheet'
import { EditPrincipalSheet } from './sheets/EditPrincipalSheet'
import {
  CheckCircle2,
  UserPlus,
  Building2,
  CreditCard,
  Trash2,
  ChevronRight,
  Pencil,
  Plus,
  FileText
} from 'lucide-react'
import { FormationProposedNameSheet } from '../../components/formations/FormationProposedNameSheet'
import { FormationBusinessAddressSheet } from '../../components/formations/FormationBusinessAddressSheet'
import { FormationContactInfoSheet } from '../../components/formations/FormationContactInfoSheet'
import { FormationBusinessAddress, FormationContactInfo as FormationContactInfoType } from '@/types'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'

function SortableProposedNameItem({ 
  pn, 
  onEdit, 
  onDelete 
}: { 
  pn: any, 
  onEdit: () => void, 
  onDelete: (e: React.MouseEvent) => void 
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: pn.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex items-center justify-between py-3 hover:bg-muted/50 -mx-4 px-4 transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <button
          className="text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing p-1 -ml-1"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
          <span className="text-xs font-medium text-muted-foreground">{pn.position + 1}</span>
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-foreground truncate">{pn.name}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={onEdit}>
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={onDelete}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  )
}

const STAGE_KEYS = ['application', 'filing', 'completed'] as const
const STAGE_LABELS: Record<string, string> = {
  application: 'Application',
  filing: 'State Filing',
  completed: 'Complete',
}


type LocationItem = { id: string; code: string; name: string }
type StateItem = LocationItem & { country_id: string }
type LocalAreaItem = LocationItem & { state_id: string }

type Props = {
  formation: FormationDetailed
  entity: FormationEntity
  supports: FormationSupports
  countries: LocationItem[]
  states: StateItem[]
  local_areas: LocalAreaItem[]
}

export default function FormationsShow({ formation, entity, supports, countries, states, local_areas }: Props) {
  const basePath = useBasePath()
  const confirm = useConfirm()
  const [addPersonOpen, setAddPersonOpen] = useState(false)
  const [editPerson, setEditPerson] = useState<typeof formation.principals[number] | null>(null)

  const [proposedNameSheetOpen, setProposedNameSheetOpen] = useState(false)
  const [editProposedName, setEditProposedName] = useState<typeof formation.proposed_names[number] | null>(null)
  const [proposedNames, setProposedNames] = useState<typeof formation.proposed_names>(formation?.proposed_names || [])

  const [businessAddressSheetOpen, setBusinessAddressSheetOpen] = useState(false)
  const [editBusinessAddress, setEditBusinessAddress] = useState<FormationBusinessAddress | null>(null)

  const [contactInfoSheetOpen, setContactInfoSheetOpen] = useState(false)
  const [editContactInfo, setEditContactInfo] = useState<FormationContactInfoType | null>(null)

  useEffect(() => {
    if (formation?.proposed_names) setProposedNames(formation.proposed_names)
  }, [formation?.proposed_names])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setProposedNames((items: FormationProposedName[]) => {
        const oldIndex = items.findIndex((i: FormationProposedName) => i.id === active.id)
        const newIndex = items.findIndex((i: FormationProposedName) => i.id === over.id)
        const newArray = arrayMove(items, oldIndex, newIndex)
        
        // Optimistically update positions for UI display
        const updatedArray = newArray.map((item: FormationProposedName, index: number) => ({ ...item, position: index }))
        
        router.patch(`${basePath}/formations/${formation.id}/proposed_names/reorder`, {
          ordered_ids: updatedArray.map((i: FormationProposedName) => i.id)
        }, { preserveScroll: true })

        return updatedArray
      })
    }
  }

  if (!formation) {
    return (
      <DashboardLayout title="Formation Not Found">
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Building2 className="w-8 h-8 text-muted-foreground/40 mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Formation not found</h3>
          <Button variant="outline" asChild>
            <Link href={`${basePath}/formations`}>Back to formations</Link>
          </Button>
        </div>
      </DashboardLayout>
    )
  }

  const totalCost = (entity?.state_fee_cents || 0) + (entity?.service_fee_cents || 0)

  return (
    <DashboardLayout title={formation.legal_name} breadcrumbs={[{ label: 'Formations', href: 'formations' }, { label: formation.legal_name }]}>
      <div>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-semibold tracking-tight">{formation.legal_name}</h1>
            <StageBadge stage={formation.stage} variant="formation" />
          </div>
          <p className="text-sm text-muted-foreground">
            {entity?.name} · {entity?.jurisdiction_code}
            {formation.assumed_name && ` · DBA: ${formation.assumed_name}`}
          </p>
          <div className="flex items-center gap-2 mt-3">
            <Button asChild size="sm" variant="outline">
              <Link href={`${basePath}/formations/${formation.id}/documents`}>
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                Documents
                {formation.documents_count > 0 && (
                  <Badge variant="secondary" className="ml-1.5 font-normal text-xs px-1.5 py-0">{formation.documents_count}</Badge>
                )}
              </Link>
            </Button>
            <Button asChild size="sm" variant="outline">
              <Link href={`${basePath}/formations/${formation.id}/payment`}>
                <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                Payment
                {formation.payment?.paid && (
                  <Badge className="ml-1.5 font-normal text-xs px-1.5 py-0 bg-green-600 text-white">Paid</Badge>
                )}
              </Link>
            </Button>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-8 py-4 px-5 border rounded-lg">
          <ProgressStepper stages={STAGE_KEYS} labels={STAGE_LABELS} currentStage={formation.stage} />
        </div>

        {/* Get Started callout - shows when payment not yet made */}
        {(() => {
          const missing: { label: string; description: string; href: string; icon: React.ReactNode }[] = []
          if (!formation.payment || !formation.payment.paid) missing.push({ label: 'Complete payment', description: `Pay ${formatCurrency(totalCost)} to begin your formation filing`, href: `${basePath}/formations/${formation.id}/payment`, icon: <CreditCard className="w-4 h-4 text-muted-foreground" /> })
          if (missing.length === 0) return null
          return (
            <div className="mb-8">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Get Started</h2>
              <div className="rounded-lg border border-dashed bg-muted/30 divide-y divide-dashed">
              {missing.map(item => (
                <Link
                  key={item.label}
                  href={item.href}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    {item.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground">{item.label}</div>
                    <div className="text-xs text-muted-foreground">{item.description}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </Link>
              ))}
              </div>
            </div>
          )
        })()}

        {/* Content sections */}
        <div className="space-y-6">

          {/* People */}
          <section className="border rounded-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">People</h2>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setAddPersonOpen(true)}>
                <UserPlus className="w-3.5 h-3.5 mr-1" />
                Add person
              </Button>
            </div>
            <div className="px-4">
              {formation.principals?.length > 0 ? (
                <div className="divide-y">
                  {formation.principals.map((p) => (
                    <div
                      key={p.id}
                      className="group flex items-center justify-between py-3 cursor-pointer hover:bg-muted/50 -mx-4 px-4 transition-colors"
                      onClick={() => setEditPerson(p)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
                          <span className="text-xs font-medium text-muted-foreground">
                            {p.full_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-foreground truncate">{p.full_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {p.email && <span>{p.email} · </span>}
                            {Object.keys(p.roles || {}).length > 0
                              ? Object.keys(p.roles).map(r => r.charAt(0).toUpperCase() + r.slice(1).replace('_', ' ')).join(', ')
                              : <span className="capitalize">{p.principal_kind}</span>
                            }
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {p.kyc_verified && (
                          <span className="flex items-center gap-1 text-xs text-green-600">
                            <CheckCircle2 className="w-3 h-3" />
                          </span>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                          onClick={async (e) => { e.stopPropagation(); if (await confirm({ description: 'Remove this person?', confirmLabel: 'Remove', destructive: true })) router.delete(`${basePath}/formations/${formation.id}/principals/${p.id}`) }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-6 text-center">
                  <p className="text-sm text-muted-foreground mb-2">No people added yet</p>
                  <Button variant="outline" size="sm" onClick={() => setAddPersonOpen(true)}>
                    Add first person
                  </Button>
                </div>
              )}
            </div>
          </section>

          {/* Proposed Names */}
          {entity && (
            <section className="border rounded-lg">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h2 className="text-sm font-medium text-foreground">Proposed Names</h2>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setEditProposedName(null); setProposedNameSheetOpen(true); }}>
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Add name
                </Button>
              </div>
              <div className="px-4">
                {proposedNames.length > 0 ? (
                  <DndContext 
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={proposedNames.map((pn: FormationProposedName) => pn.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="divide-y">
                        {proposedNames.map((pn: FormationProposedName) => (
                          <SortableProposedNameItem
                            key={pn.id}
                            pn={pn}
                            onEdit={() => { setEditProposedName(pn); setProposedNameSheetOpen(true); }}
                            onDelete={async (e) => { 
                              e.stopPropagation(); 
                              if (await confirm({ description: 'Remove this proposed name?', confirmLabel: 'Remove', destructive: true })) {
                                router.delete(`${basePath}/formations/${formation.id}/proposed_names/${pn.id}`)
                              }
                            }}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <div className="py-6 text-center">
                    <p className="text-sm text-muted-foreground mb-2">No proposed names added yet</p>
                    <Button variant="outline" size="sm" onClick={() => { setEditProposedName(null); setProposedNameSheetOpen(true); }}>
                      Add first choice
                    </Button>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Business Details */}
          <div className="mb-8">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Business Details</h2>
            <div className="border rounded-lg divide-y">
              {/* Address */}
              <div className="group flex items-start gap-4 px-4 py-3">
                <span className="text-xs text-muted-foreground w-28 shrink-0 pt-0.5">Address</span>
                <div className="flex-1 min-w-0">
                  {formation.business_addresses?.length > 0 ? (
                    <div className="space-y-2">
                      {formation.business_addresses.map((addr: FormationBusinessAddress) => (
                        <div key={addr.id} className="group/item flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            {addr.label && <span className="text-xs text-muted-foreground mr-1.5">{addr.label}:</span>}
                            <span className="text-sm text-foreground">{addr.street_address_1}</span>
                            {addr.street_address_2 && <span className="text-sm text-muted-foreground">, {addr.street_address_2}</span>}
                            <span className="text-sm text-muted-foreground">, {addr.city}{addr.state_province && `, ${addr.state_province}`} {addr.postal_code} · {addr.country_code}</span>
                          </div>
                          <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover/item:opacity-100 transition-opacity">
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setEditBusinessAddress(addr); setBusinessAddressSheetOpen(true) }}>
                              <Pencil className="w-3 h-3 text-muted-foreground" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={async () => { if (await confirm({ description: 'Remove this address?', confirmLabel: 'Remove', destructive: true })) router.delete(`${basePath}/formations/${formation.id}/business_addresses/${addr.id}`) }}>
                              <Trash2 className="w-3 h-3 text-muted-foreground" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </div>
                <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => { setEditBusinessAddress(null); setBusinessAddressSheetOpen(true) }}>
                  <Plus className="w-3 h-3 text-muted-foreground" />
                </Button>
              </div>

              {/* Contact Info */}
              <div className="group flex items-start gap-4 px-4 py-3">
                <span className="text-xs text-muted-foreground w-28 shrink-0 pt-0.5">Contact</span>
                <div className="flex-1 min-w-0">
                  {formation.contact_infos?.length > 0 ? (
                    <div className="space-y-2">
                      {formation.contact_infos.map((ci: FormationContactInfoType) => (
                        <div key={ci.id} className="group/item flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            {ci.label && <span className="text-xs text-muted-foreground mr-1.5">{ci.label}:</span>}
                            <span className="text-sm text-foreground">{ci.email}</span>
                            {ci.phone && <span className="text-sm text-muted-foreground"> · {ci.phone}</span>}
                          </div>
                          <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover/item:opacity-100 transition-opacity">
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setEditContactInfo(ci); setContactInfoSheetOpen(true) }}>
                              <Pencil className="w-3 h-3 text-muted-foreground" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={async () => { if (await confirm({ description: 'Remove this contact?', confirmLabel: 'Remove', destructive: true })) router.delete(`${basePath}/formations/${formation.id}/contact_infos/${ci.id}`) }}>
                              <Trash2 className="w-3 h-3 text-muted-foreground" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </div>
                <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => { setEditContactInfo(null); setContactInfoSheetOpen(true) }}>
                  <Plus className="w-3 h-3 text-muted-foreground" />
                </Button>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Slideouts */}
      <AddPrincipalSheet
        formationId={formation.id}
        formationName={formation.legal_name}
        supports={supports}
        countries={countries}
        states={states}
        localAreas={local_areas}
        open={addPersonOpen}
        onOpenChange={setAddPersonOpen}
      />
      <EditPrincipalSheet
        formationId={formation.id}
        formationName={formation.legal_name}
        supports={supports}
        principal={editPerson}
        countries={countries}
        states={states}
        localAreas={local_areas}
        open={!!editPerson}
        onOpenChange={(open) => { if (!open) setEditPerson(null) }}
      />
      <FormationProposedNameSheet
        open={proposedNameSheetOpen}
        onOpenChange={setProposedNameSheetOpen}
        formationId={formation.id}
        proposedName={editProposedName}
        basePath={basePath}
      />
      <FormationBusinessAddressSheet
        open={businessAddressSheetOpen}
        onOpenChange={setBusinessAddressSheetOpen}
        formationId={formation.id}
        address={editBusinessAddress}
        basePath={basePath}
        countries={countries}
        states={states}
      />
      <FormationContactInfoSheet
        open={contactInfoSheetOpen}
        onOpenChange={setContactInfoSheetOpen}
        formationId={formation.id}
        contact={editContactInfo}
        basePath={basePath}
      />
    </DashboardLayout>
  )
}
